import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { isCommentExists } from '@lib/core/comments/filters';
import { useCommentsHandler } from '@lib/core/comments/hooks/useCommentsHandler';
import { selectAllFeedbacksByProductId } from '@lib/core/products/selectors/productFeedback';
import { TParsedProductInstance, TProductInstance } from '@lib/core/products/types';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { useApp } from '@lib/core/service/hooks';
import { useWidgetInstance } from '@lib/core/service/hooks/useWidgetInstance';
import { getVinhoodTagsFromCharacteristics, isAppInIframe } from '@lib/core/service/utils';
import { useProductList, useProductRate, useUser } from '@lib/core/users/hooks';
import { useClickTracker } from '@lib/tools/clickTracker/hooks/clickTracker';
import { FIND_ME_BUTTON_CLICK } from '@lib/tools/clickTracker/slice/clickTracker';
import { selectCurrentGameProgress } from '@lib/tools/gamification/selectors';
import { actionSelectGameProduct } from '@lib/tools/gamification/slices';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { LocaleUtils } from '@lib/tools/locale/utils';
import { ILocaleText } from '@lib/tools/locale/views/LocaleFragment';
import { DIETARY_PREFERENCE_EXPOSURE, PRODUCT_CATEGORIES } from '@lib/tools/shared/helpers/consts';
import { isProductCategory } from '@lib/tools/shared/helpers/interfaces';
import { useAddons } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';

import AlcoholIcon from '@components/web/src/assets/icons/largeProductPage/alcohol.svg';
import AromaIcon from '@components/web/src/assets/icons/largeProductPage/aroma.svg';
import FromIcon from '@components/web/src/assets/icons/largeProductPage/from.svg';
import GrapeIcon from '@components/web/src/assets/icons/largeProductPage/grape.svg';
import StyleBeerIcon from '@components/web/src/assets/icons/largeProductPage/styleBeer.svg';
import StyleCoffeeIcon from '@components/web/src/assets/icons/largeProductPage/styleCoffee.svg';
import TasteIcon from '@components/web/src/assets/icons/largeProductPage/taste.svg';

export interface IIdentityForKiosk {
  title: ILocaleText;
  description: string;
  icon: string;
  order: number;
}
interface IIdentity {
  title: ILocaleText;
  description: string;
}

export interface IIdentitiesForWeb {
  identityWarningText: ILocaleText;
  productIdentities: IIdentity[];
}

export const parseBasicProductData = (productInstanceData: TProductInstance) => {
  const {
    product,
    promotions,
    price,
    identifier,
    preferences,
    url,
    character,
    discount,
    taste_match: tasteMatch,
    ecommerce_id: productEcommerceId,
    description: productInstanceDescription,
    tags,
  } = productInstanceData || {};

  const {
    name,
    image,
    attributes,
    producer,
    characteristics,
    category,
    // product_category is only for the limited catalog and it will be removed
    product_category: pc,
    description,
    format,
    // character is only for the limited catalog and it will be removed
    character: productCharacter,
    region,
    regions,
  } = product || {};
  const productId = product?.identifier || '';
  const productInstanceId = identifier || '';

  const possibleProductCategory = category || pc;
  const productCategory = isProductCategory(possibleProductCategory) ? possibleProductCategory : null;
  // this is needed until we don't remove the limited catalog, then we have to refactor it
  const productCharacterName = character?.name || productCharacter?.name || '';
  const productCharacterDescription = character?.character_description || character?.charDesc || '';
  const productCharacterId = character?.identifier || product?.character?.identifier || '';
  const productCharacterTypeIdentifier = character?.type?.identifier;
  // this is needed until we don't remove the limited catalog, then we have to refactor it
  const productCharacterTechnicalCaption = character?.techCaption || productCharacter?.technical_caption || '';
  const productCharacterImage = character?.image || productCharacter?.image || '';
  const productProducerName = producer?.name || '';
  const productFormatName = format?.name || '';
  const productDescription = description || '';
  const productFormatIdentifier = format?.identifier || '';
  // this is needed until we don't remove the limited catalog, then we have to refactor it
  const productRegionName = (!!region?.length && region[0]?.name) || (!!regions?.length && regions[0]?.name) || '';
  const productRegionCountry =
    (!!region?.length && region[0]?.country) || (!!regions?.length && regions[0]?.country) || '';
  //
  const productProducerUrl = url || '';
  const productTasteMatchValue = typeof tasteMatch === 'number' ? tasteMatch : null;

  const productLocationMap = tags?.image || '';
  const productPriceCurrency = (typeof price !== 'string' && price?.currency) || productInstanceData?.currency || '';
  const productPriceCurrencySymbol = LocaleUtils.getCurrencySymbolFromCode(productPriceCurrency);
  const productDiscountPriceCurrency = discount?.price?.currency || '';
  const productDiscountPriceCurrencySymbol = LocaleUtils.getCurrencySymbolFromCode(productDiscountPriceCurrency);
  const productOriginalPrice = (typeof price !== 'string' && Number(price?.value)) || Number(price) || 0;
  const productDiscountPrice = Number(discount?.price?.value) || Number(discount?.price) || 0;
  const productOriginalPriceAndCurrency = productOriginalPrice
    ? `${productOriginalPrice} ${productPriceCurrencySymbol}`
    : null;
  const productDiscountPriceAndCurrency = productDiscountPrice
    ? `${productDiscountPrice} ${productDiscountPriceCurrencySymbol}`
    : null;

  const productTags = getVinhoodTagsFromCharacteristics(characteristics || []);
  const productCharacteristicsNames = characteristics?.map(v => v.name) || [];
  const productBadges = preferences
    ? preferences.filter(item => item.exposure === DIETARY_PREFERENCE_EXPOSURE.TAGS).map(tag => tag.name)
    : [];
  const productPreferencesSlug = preferences
    ? preferences.filter(item => item.exposure === DIETARY_PREFERENCE_EXPOSURE.TAGS).map(tag => tag.slug)
    : [];

  return {
    attributes,
    character,
    producer,
    productBadges,
    productCategory,
    productCharacterDescription,
    productCharacterId,
    productCharacterImage,
    productCharacterName,
    productCharacterTechnicalCaption,
    productCharacterTypeIdentifier,
    productCharacteristicsNames,
    productDescription,
    productDiscountPrice,
    productDiscountPriceAndCurrency,
    productDiscountPriceCurrency,
    productDiscountPriceCurrencySymbol,
    productEcommerceId,
    productFormat: format || null,
    productFormatIdentifier,
    productFormatName,
    productId,
    productImage: image || '',
    productInstanceDescription,
    productInstanceId,
    productLocationMap,
    productName: name || '',
    productOriginalPrice,
    productOriginalPriceAndCurrency,
    productPreferencesSlug,
    productPriceCurrency,
    productPriceCurrencySymbol,
    productProducerName,
    productProducerUrl,
    productRegionCountry,
    productRegionName,
    productTags,
    productTasteMatchValue,
    promotions,
    region,
    regions,
  };
};

export const useParsedProducts = () => {
  const dispatch = useTypedDispatch();

  const { locale } = useApp();
  const { isUserAuthenticated, isUserHasCharacterForCurrentPC } = useUser();
  // Comment Data
  const {
    isCommentUpdateLoading,
    commentsList,
    isCommentsDataLoading,
    isNewCommentsDataFetching,
    handleComment,
    setIsNewCommentsDataFetching,
  } = useCommentsHandler();
  const { associatedRetailerLocation } = useRetailerLocation();
  const currentGameProgress = useSelector(selectCurrentGameProgress);
  const answeredProductsFeedbacksByProductsId = useSelector(selectAllFeedbacksByProductId);
  // Wishlist Data
  const { wishlistProductInstanceIds, isWishlistProductListLoading, handleUpdateWishlistProductListFunction } =
    useProductList();
  // Feedback Data
  const {
    handleUpdateProductRating,
    productRateData,
    setDiscoveryQuizProducts,
    productRateOptions,
    isProductRateDataLoading,
    lastUpdatedProductRatingId,
    isProductRateUpdateLoading,
  } = useProductRate();

  const {
    isEnableLocationMapAddon,
    isEnableVusionAddon,
    isShowProductLocationAddon,
    isInteractiveTastingAddon,
    isPriceRangeAddon,
    isAddToCartAddon,
    isEventsGamingAddon,
    isDisableUserPersistenceAddon,
    isFreeFormTastingAddon,
  } = useAddons();
  const isGameAddon = isEventsGamingAddon || isFreeFormTastingAddon;

  const { handleRecordClickTime, productsFindMeLastClickTime } = useClickTracker();
  const { handleSetProductInteraction, handleResetProductInteraction, productInteraction } = useWidgetInstance();

  const parseProduct = useCallback(
    (productInstanceData: TProductInstance) => {
      if (!productInstanceData) return null;

      const {
        producer,
        productBadges,
        productCategory,
        productCharacterId,
        productCharacterName,
        productCharacterImage,
        productCharacterTechnicalCaption,
        productCharacterDescription,
        productCharacterTypeIdentifier,
        productCharacteristicsNames,
        productPriceCurrency,
        productPriceCurrencySymbol,
        productDiscountPrice,
        productEcommerceId,
        productFormat,
        productFormatIdentifier,
        productFormatName,
        productId,
        productImage,
        productInstanceId,
        productName,
        productOriginalPrice,
        productPreferencesSlug,
        productProducerName,
        productProducerUrl,
        productRegionCountry,
        productRegionName,
        productDescription,
        productTags,
        productTasteMatchValue,
        promotions,
        region,
        productDiscountPriceCurrency,
        productDiscountPriceCurrencySymbol,
        regions,
        productDiscountPriceAndCurrency,
        productOriginalPriceAndCurrency,
        productLocationMap,
        productInstanceDescription,
        character,
        attributes,
      } = parseBasicProductData(productInstanceData);

      const productPerfectFor = (locale && character?.attributes?.[`when_to_drink_${locale}`]) || '';
      const minCelsiusTemperature = character?.attributes?.ideal_temperature_min_celsius || 0;
      const maxCelsiusTemperature = character?.attributes?.ideal_temperature_max_celsius || 0;
      const alcohol = attributes?.alcohol || '';
      const productFormatSize = attributes?.quantity || '';
      const productStyle = (locale && attributes?.[`style_${locale}`]) || '';
      const productBackgroundImage = attributes?.background_image || '';
      const commentFromList = commentsList?.find(commentFromArray => isCommentExists(commentFromArray, productId));
      const isCommentInList = !!commentFromList;
      const productsFindMeLastClickTimeById = productsFindMeLastClickTime[productInstanceId];
      const now = Date.now();
      const ONE_DAY_MS = 24 * 60 * 60 * 1000;

      const isMobileKiosk = !!associatedRetailerLocation;
      const isFindMeClicked = productsFindMeLastClickTimeById && now - productsFindMeLastClickTimeById < ONE_DAY_MS;

      const handleRecordFindMeClickTimeById = () =>
        handleRecordClickTime({
          clickType: FIND_ME_BUTTON_CLICK,
          id: productInstanceId,
          isClicked: isFindMeClicked,
          now,
        });

      const handleSetDiscoveryQuizProducts = () => setDiscoveryQuizProducts(productInstanceData);
      const handleSelectGameProduct = () => dispatch(actionSelectGameProduct(productInstanceData));
      const isUsedInGame = currentGameProgress?.usedProductIds.includes(productInstanceData.product.identifier);
      const isGameEnabled = isGameAddon && isUserAuthenticated && isUserHasCharacterForCurrentPC;

      const isProductInstanceInWishlist = wishlistProductInstanceIds?.includes(productId);
      const handleUpdateWishlistProductList = () => {
        handleUpdateWishlistProductListFunction(productId, productName);
        if (isAppInIframe) {
          if (!isProductInstanceInWishlist) {
            handleSetProductInteraction({
              productId: productInstanceId,
            });
          }
          if (isProductInstanceInWishlist && productInteraction?.productId === productInstanceId) {
            handleResetProductInteraction();
          }
        }
      };
      const productRate = productRateData.find(
        singleFeedback => singleFeedback?.product?.identifier === productInstanceId,
      )?.rate;
      const isProductFeedbackFinished = answeredProductsFeedbacksByProductsId[productId]?.length === 3;

      const {
        productIdentitiesCard: {
          warningText: identityWarningText,
          notPresentText,
          typeTitleText,
          originTitleText,
          grapeTitleText,
          colorTitleText,
          alcoholTitleText,
          aromasTitleText,
          tasteTitleText,
          glassTitleText,
          temperatureTitleText,
          allergensTitleText,
          fermentationTitleText,
          specialIngredientTitleText,
          varietyTitleText,
          roastedTitleText,
          styleTitleText,
          fromTitleText,
        },
        productPage,
        commonMessages,
      } = localeCommon;

      const { publishedTerms } = LocaleUtils;
      const { vusionAddonDefaultDescription } = commonMessages;

      const vusionAddonDefaultDescriptionText = publishedTerms[vusionAddonDefaultDescription.id];

      const productLocationDescription =
        (isEnableVusionAddon
          ? productInstanceDescription || vusionAddonDefaultDescriptionText
          : productInstanceDescription) || '';

      const isFindProductFunctionalityEnable = !!(
        ((isShowProductLocationAddon || isEnableVusionAddon) && productLocationDescription) ||
        ((productLocationMap || productLocationDescription) && isEnableLocationMapAddon)
      );

      const notPresentTextStatic = publishedTerms[notPresentText.id];

      // KIOSK IDENTITIES DATA
      const identitiesForKioskRawData: IIdentityForKiosk[] = [
        {
          description: attributes?.[`aromas_${locale}`] || attributes?.[`aroma_${locale}`],
          icon: AromaIcon,
          order: 4,
          title: aromasTitleText,
        },
      ];

      if (productRegionName && productRegionCountry) {
        identitiesForKioskRawData.unshift({
          description: `${productRegionName}, ${productRegionCountry}`,
          icon: FromIcon,
          order: 1,
          title: fromTitleText,
        });
      }

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_WINE) {
        identitiesForKioskRawData.push(
          { description: attributes?.[`type_${locale}`], icon: GrapeIcon, order: 2, title: grapeTitleText },
          { description: alcohol ? `${alcohol}%` : '', icon: AlcoholIcon, order: 3, title: alcoholTitleText },
        );
      }

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_BEER) {
        identitiesForKioskRawData.push(
          {
            description: productStyle,
            icon: StyleBeerIcon,
            order: 2,
            title: styleTitleText,
          },
          { description: alcohol ? `${alcohol}%` : '', icon: AlcoholIcon, order: 3, title: alcoholTitleText },
        );
      }

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_COFFEE) {
        identitiesForKioskRawData.push(
          {
            description: productStyle,
            icon: StyleCoffeeIcon,
            order: 2,
            title: styleTitleText,
          },
          { description: attributes?.[`taste_${locale}`], icon: TasteIcon, order: 3, title: tasteTitleText },
        );
      }

      const identitiesForKiosk = identitiesForKioskRawData
        .filter(({ description }) => description)
        .sort((a, b) => a.order - b.order);

      // WEB IDENTITIES DATA
      const identitiesForWeb: IIdentitiesForWeb = {
        identityWarningText,
        productIdentities: [
          { description: attributes?.[`type_${locale}`], title: typeTitleText },
          { description: `${productRegionName}, ${productRegionCountry}`, title: originTitleText },
          { description: attributes?.[`color_${locale}`], title: colorTitleText },
          {
            description: attributes?.[`aromas_${locale}`] || attributes?.[`aroma_${locale}`],
            title: aromasTitleText,
          },
          { description: attributes?.[`taste_${locale}`], title: tasteTitleText },
        ],
      };

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_WINE) {
        identitiesForWeb.productIdentities.push(
          { description: attributes?.[`variety_${locale}`], title: grapeTitleText },
          { description: alcohol ? `${alcohol}%` : '', title: alcoholTitleText },
          { description: character?.attributes?.[`ideal_glass_${locale}`], title: glassTitleText },
          {
            description: `${minCelsiusTemperature}° - ${maxCelsiusTemperature}°`,
            title: temperatureTitleText,
          },
          {
            description: attributes?.[`allergens_${locale}`] || notPresentTextStatic,
            title: allergensTitleText,
          },
        );
      }

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_BEER) {
        identitiesForWeb.productIdentities.push(
          { description: attributes?.[`variety_${locale}`], title: fermentationTitleText },
          { description: alcohol ? `${alcohol}%` : '', title: alcoholTitleText },
          {
            description: character?.attributes?.[`special_ingredient_${locale}`],
            title: specialIngredientTitleText,
          },
          { description: character?.attributes?.[`ideal_glass_${locale}`], title: glassTitleText },
          {
            description: `${minCelsiusTemperature}° - ${maxCelsiusTemperature}°`,
            title: temperatureTitleText,
          },
          {
            description: attributes?.[`allergens_${locale}`] || notPresentTextStatic,
            title: allergensTitleText,
          },
        );
      }

      if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_COFFEE) {
        identitiesForWeb.productIdentities.push(
          { description: attributes?.[`variety_${locale}`], title: varietyTitleText },
          { description: attributes?.[`transformation_location_${locale}`], title: roastedTitleText },
          {
            description: attributes?.[`allergens_${locale}`] || notPresentTextStatic,
            title: allergensTitleText,
          },
        );
      }

      const preparationsClassicText = productPage.preparationsClassic;
      const preparationsSpecialText = productPage.preparationsSpecial;

      const preparationsData =
        productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_COFFEE
          ? [
              {
                description: character?.preparationClassic?.technicalCaption,
                image: character?.preparationClassic?.metadata?.thumbnail,
                subtitle: preparationsClassicText,
                title: character?.preparationClassic?.characterName,
              },
              {
                description: character?.preparationSpecialty?.technicalCaption,
                image: character?.preparationSpecialty?.metadata?.thumbnail,
                subtitle: preparationsSpecialText,
                title: character?.preparationSpecialty?.characterName,
              },
            ]
          : [];

      const fullProductData: TParsedProductInstance = {
        // Remove TParsedProductInstance type
        attributes,
        commentFromList,
        handleRecordFindMeClickTimeById,
        handleSelectGameProduct,
        handleSetDiscoveryQuizProducts,
        handleSubmitComment: handleComment,
        handleUpdateProductRating,
        handleUpdateWishlistProductList,
        identitiesForKiosk,
        identitiesForWeb,
        isAddToCartAddon,
        isCommentInList,
        isCommentUpdateLoading,
        isCommentsDataLoading,
        isDisableUserPersistenceAddon,
        isEnableLocationMapAddon,
        isEnableVusionAddon,
        isFindMeClicked,
        isFindProductFunctionalityEnable,
        isGameEnabled,
        isInteractiveTastingAddon,
        isMobileKiosk,
        isNewCommentsDataFetching,
        isPriceRangeAddon,
        isProductInstanceInWishlist,
        isProductRateDataLoading,
        isProductRateUpdateLoading,
        isUsedInGame,
        isWishlistProductListLoading,
        lastUpdatedProductRatingId,
        preparationsData,
        producer,
        productBackgroundImage,
        productBadges,
        productCategory,
        productCharacterDescription,
        productCharacterId,
        productCharacterImage,
        productCharacterName,
        productCharacterTechnicalCaption,
        productCharacterTypeIdentifier,
        productCharacteristicsNames,
        productDescription,
        productDiscountPrice,
        productDiscountPriceAndCurrency,
        productDiscountPriceCurrency,
        productDiscountPriceCurrencySymbol,
        productEcommerceId,
        productFeedbackStatus: {
          isDisabled: !isInteractiveTastingAddon,
          isEnabled: isInteractiveTastingAddon && !isProductFeedbackFinished,
          isFinished: isInteractiveTastingAddon && isProductFeedbackFinished,
        },
        productFormat,
        productFormatIdentifier,
        productFormatName,
        productFormatSize,
        productId,
        productImage,
        productInstanceData,
        productInstanceId,
        productLocationDescription,
        productLocationMap,
        productName,
        productOriginalPrice,
        productOriginalPriceAndCurrency,
        productPerfectFor,
        productPreferencesSlug,
        productPriceCurrency,
        productPriceCurrencySymbol,
        productProducerName,
        productProducerUrl,
        productRate,
        productRateOptions,
        productRegionCountry,
        productRegionName,
        productStyle,
        productTags,
        productTasteMatchValue,
        promotions,
        region,
        regions,
        setIsNewCommentsDataFetching,
      };

      return fullProductData;
    },
    [
      isEnableLocationMapAddon,
      isCommentsDataLoading,
      isNewCommentsDataFetching,
      commentsList,
      locale,
      wishlistProductInstanceIds,
      isWishlistProductListLoading,
      isEnableVusionAddon,
      isShowProductLocationAddon,
      productRateData,
      isProductRateDataLoading,
      lastUpdatedProductRatingId,
      isInteractiveTastingAddon,
      answeredProductsFeedbacksByProductsId,
      isProductRateUpdateLoading,
      productRateOptions,
      isCommentUpdateLoading,
      productsFindMeLastClickTime,
      isPriceRangeAddon,
      isAddToCartAddon,
    ],
  );
  const parseProductsArray = (products: TProductInstance[]) => {
    if (!products) return null;

    const parsedProducts = products.map(productInstanceData => parseProduct(productInstanceData));

    return parsedProducts;
  };
  return { parseProduct, parseProductsArray };
};
